// Discovers the full reference-strategy token universe via the same
// `list_prices` RPC the perps timeseries widget uses (timeseries-provider.tsx's
// fetchPerpsList) — DERIBIT/BINANCE/MEXC perpetuals, ~80+ tokens total,
// rather than the previous fixed 6-token list. One call, not one per token.
//
// Confirmed live 2026-07-14: DERIBIT lists both a bare ticker and a
// "_USDC"-suffixed one for some majors (e.g. 'BTC' and 'BTC_USDC') — these
// are the same underlying asset on two different perpetual contracts, so
// only one reference row should show per underlying, not two. Dedup
// priority is DERIBIT (existing, proven source for the original 6) >
// BINANCE > MEXC, and within DERIBIT prefer the bare ticker over the
// "_USDC" form (matching the original hardcoded majors).
import { useContext, useEffect, useRef, useState } from 'react';

import { WebSocketContext } from '../../websocket-context/websocket-provider';
import { IPortfolioPerformanceAccounts } from '../portfolio-performance.types';
import { makeReferenceAccount } from './reference-strategy.constants';

export interface ReferenceStrategyToken {
  // Exact string to pass as `underlying` to the `candlestick` RPC — as
  // returned by list_prices, unmodified.
  underlying: string;
  // Source to pass as `source` to the `candlestick` RPC.
  source: 'DERIBIT' | 'BINANCE' | 'MEXC';
  // Bare display symbol (suffix stripped) — what CryptoIcon/token-icon-sources
  // look up against and what shows in the account name.
  displaySymbol: string;
  account: IPortfolioPerformanceAccounts;
}

const DEDUP_SOURCE_PRIORITY: Record<string, number> = { DERIBIT: 0, BINANCE: 1, MEXC: 2 };

const stripSuffix = (token: string, source: string): string => {
  if (source === 'DERIBIT') return token.replace(/_USDC$/, '');
  if (source === 'MEXC') return token.replace(/_USDT$/, '');
  if (source === 'BINANCE') return token.replace(/USDT$/, '');
  return token;
};

export const useReferenceStrategyTokens = (): {
  tokens: ReferenceStrategyToken[];
  isLoading: boolean;
} => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const [tokens, setTokens] = useState<ReferenceStrategyToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const wsRef = useRef({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  useEffect(() => {
    wsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };
  });

  useEffect(() => {
    if (!isConnected) return;
    let cancelled = false;
    let settled = false;
    const reqId = `PP_REFERENCE_TOKENS_${Date.now()}`;
    const finish = () => {
      settled = true;
      const unsub = wsRef.current.unsubscribeFromChannel;
      if (unsub) {
        try {
          unsub(reqId);
        } catch {
          /* ignore */
        }
      }
    };

    wsRef.current.subscribeToChannel(reqId, (raw: unknown) => {
      if (cancelled || settled) return;
      const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
      const result = resp?.result as Record<string, unknown> | undefined;
      const sources = result?.sources;
      if (!Array.isArray(sources)) {
        setIsLoading(false);
        finish();
        return;
      }

      // Flatten every {SOURCE: {underlying: [...]}} entry, then dedup by
      // display symbol, keeping the highest-priority source's entry.
      const bySymbol = new Map<
        string,
        { underlying: string; source: string; displaySymbol: string }
      >();
      for (const sourceEntry of sources) {
        if (!sourceEntry || typeof sourceEntry !== 'object') continue;
        const sourceName = Object.keys(sourceEntry)[0];
        if (!sourceName || !(sourceName in DEDUP_SOURCE_PRIORITY)) continue;
        const underlyingList = (sourceEntry as Record<string, { underlying?: string[] }>)[
          sourceName
        ]?.underlying;
        if (!Array.isArray(underlyingList)) continue;

        for (const underlying of underlyingList) {
          const displaySymbol = stripSuffix(underlying, sourceName);
          const existing = bySymbol.get(displaySymbol);
          if (
            !existing ||
            DEDUP_SOURCE_PRIORITY[sourceName] < DEDUP_SOURCE_PRIORITY[existing.source]
          ) {
            bySymbol.set(displaySymbol, { underlying, source: sourceName, displaySymbol });
          }
        }
      }

      const nextTokens: ReferenceStrategyToken[] = Array.from(bySymbol.values())
        .sort((a, b) => a.displaySymbol.localeCompare(b.displaySymbol))
        .map((t) => ({
          underlying: t.underlying,
          source: t.source as ReferenceStrategyToken['source'],
          displaySymbol: t.displaySymbol,
          account: makeReferenceAccount(t.displaySymbol, 'Buy and Hold'),
        }));

      setTokens(nextTokens);
      setIsLoading(false);
      finish();
    });

    wsRef.current.sendMessage(
      JSON.stringify({
        jsonrpc: '2.0',
        id: reqId,
        method: 'list_prices',
        params: { source: [] },
      }),
    );

    const timeout = setTimeout(() => {
      if (cancelled || settled) return;
      setIsLoading(false);
      finish();
    }, 15000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      finish();
    };
  }, [isConnected]);

  return { tokens, isLoading };
};
